import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";

import { completeBuyerSignin } from "@/features/buyerAuth";
import {
  createReservation,
  type PickupPreference,
} from "@/features/storefront";
import { useShopCart } from "./shop";

const SearchParams = (search: Record<string, unknown>) => ({
  token: typeof search.token === "string" ? search.token : undefined,
  return: typeof search.return === "string" ? search.return : undefined,
});

// TNK-167 phase C — verify the magic link. Reads `?token=` from the URL,
// POSTs to /api/buyer/auth/complete, then navigates the buyer to either
// the optional `?return=` (the original destination they were trying to
// reach before the login bounce) or the account index. The completion
// effect uses a ref-guard so React 19's double-effect-in-dev doesn't
// consume the same single-use token twice.
export const Route = createFileRoute("/shop/account/verify")({
  component: ShopAccountVerify,
  validateSearch: SearchParams,
});

function ShopAccountVerify() {
  const { token, return: returnTo } = Route.useSearch();
  const navigate = useNavigate();
  const cart = useShopCart();
  const ranRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    if (!token) {
      setError("Missing sign-in token.");
      return;
    }
    void completeBuyerSignin(token)
      .then(async ({ reservePayload }) => {
        // TNK-202 — if this link carried a guest cart, finish the reservation
        // now (under the fresh session) and land on it. Built from the
        // server-carried payload, so it works even on a different device than
        // the one that requested the link.
        if (reservePayload && reservePayload.cardIds.length > 0) {
          try {
            const { id } = await createReservation({
              cardIds: reservePayload.cardIds,
              buyerName: reservePayload.buyerName,
              buyerNote: reservePayload.buyerNote,
              pickupPreference: reservePayload.pickupPreference as
                | PickupPreference
                | undefined,
              shippingAddress: reservePayload.shippingAddress,
            });
            cart.clear();
            void navigate({
              to: "/shop/account/reservations/$id",
              params: { id },
            });
            return;
          } catch {
            // Reservation couldn't be created (e.g. every card sold during the
            // window) — the buyer is still signed in; send them to the account
            // page rather than stranding them on a spinner.
            void navigate({ to: "/shop/account" });
            return;
          }
        }
        void navigate({
          to: returnTo && returnTo.startsWith("/") ? returnTo : "/shop/account",
        });
      })
      .catch((err: unknown) => {
        setError(
          err instanceof Error
            ? err.message
            : "Could not sign in with that link.",
        );
      });
  }, [token, returnTo, navigate, cart]);

  if (error) {
    return (
      <div className="mx-auto max-w-md space-y-4 py-12 text-center">
        <h1 className="shop-display text-2xl font-bold text-foreground">
          Sign-in failed
        </h1>
        <p className="text-sm text-destructive">{error}</p>
        <Link
          to="/shop/account/login"
          search={{ return: undefined }}
          className="text-sm font-medium text-primary hover:underline"
        >
          Request a new sign-in link
        </Link>
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-md py-12 text-center">
      <p className="text-muted-foreground">Signing you in…</p>
    </div>
  );
}
